import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { UiButtonStyle } from 'components/Ui/UiButton'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { useAppDispatch } from 'helpers/store'
import { ModalSize } from 'types/enums/ModalSize'
import { toast } from 'utils/toastUtils'
import { OffersAction } from 'store/offers/OffersAction'
import { extractReservationInfo } from 'utils/offers/offersUtils'
import type { OfferCreateReservationResponse } from 'types/entities/Offer'

export const useOfferReservationSuccessModal = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    return useCallback(
        (response: OfferCreateReservationResponse, offerId: number) => {
            const { reservationId, reservationGuid } = extractReservationInfo(response)

            const refreshOfferAndClose = () => {
                dispatch(closeSecondaryModal())
                dispatch(OffersAction.fetchOfferById(offerId))
            }

            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Do you want to open the reservation detail?')}
                        message={
                            <div className="space-y-2">
                                <p>{t('Mail has been sent')}</p>
                                <p>
                                    {t('Reservation was successfully created:')}
                                    {reservationGuid ? (
                                        <>
                                            {' '}
                                            <span className="font-mono font-semibold text-gray-900 dark:text-white">
                                                #{reservationGuid}
                                            </span>
                                        </>
                                    ) : null}
                                </p>
                            </div>
                        }
                        confirmLabel={t('Yes') as string}
                        cancelLabel={t('No') as string}
                        confirmStyle={UiButtonStyle.Green}
                        onCancel={refreshOfferAndClose}
                        onConfirm={() => {
                            dispatch(closeSecondaryModal())
                            if (reservationId) {
                                navigate(`/reservations/detail/${reservationId}`)
                                return
                            }
                            toast.warning(t('Reservation ID not found, cannot navigate'))
                            dispatch(OffersAction.fetchOfferById(offerId))
                        }}
                    />,
                    {
                        modalSize: ModalSize.Small,
                        closeModal: refreshOfferAndClose,
                    },
                    t('Reservation Created')
                )
            )
        },
        [dispatch, navigate, t]
    )
}
